import type { OtpRequest, OtpRequestResult, SignInResult } from '@fruuit/shared'
import { useQuery } from '@tanstack/react-query'
import { API_BASE, type ApiError, api, safeNext } from './client'
import type { AuthProviders, Me } from './types'

export const meQueryKey = ['me'] as const
export const providersQueryKey = ['auth', 'providers'] as const

/** `GET /api/v1/me`. A 401 is an answer here (anonymous), so it neither retries nor redirects. */
export function useMe() {
  return useQuery<Me, ApiError>({
    queryKey: meQueryKey,
    queryFn: ({ signal }) => api.get<Me>('/me', { signal, redirectOnUnauthorized: false }),
    staleTime: 60_000,
    retry: false,
  })
}

/** `GET /api/v1/auth/providers`: which sign-in buttons to show. */
export function useAuthProviders() {
  return useQuery<AuthProviders, ApiError>({
    queryKey: providersQueryKey,
    queryFn: ({ signal }) =>
      api.get<AuthProviders>('/auth/providers', { signal, redirectOnUnauthorized: false }),
    staleTime: 5 * 60_000,
  })
}

export type OAuthProvider = 'google' | 'microsoft'

/** Full-page navigation target that starts an OAuth flow (wired by the auth lane, A-02/A-03). */
export function oauthStartUrl(provider: OAuthProvider, next?: string | null, invite?: string) {
  const params = new URLSearchParams()
  params.set('next', safeNext(next) ?? '/')
  if (invite) params.set('invite', invite)
  return `${API_BASE}/auth/oauth/${provider}/start?${params.toString()}`
}

// Email sign-in (A-04, ADR-D08): `POST /auth/otp/request` always answers 202 and says whether a
// code was emailed or the user's authenticator app supplies it; verify sets the session.
export function requestSignInCode(body: OtpRequest, context: { requestId: string }) {
  return api.post<OtpRequestResult>('/auth/otp/request', body, {
    requestId: context.requestId,
    redirectOnUnauthorized: false,
  })
}

export function verifySignInCode(
  body: { email: string; code: string; next?: string },
  context: { requestId: string },
) {
  return api.post<SignInResult>('/auth/otp/verify', body, {
    requestId: context.requestId,
    redirectOnUnauthorized: false,
  })
}

/** Ends the session, then reloads into the sign-in page (clears every client cache, NAV-009). */
export async function signOut(): Promise<void> {
  try {
    await api.post<void>('/auth/signout', undefined, { redirectOnUnauthorized: false })
  } finally {
    window.location.assign('/signin')
  }
}
